type DccJson = any;

export type DccSearchType = 'all' | 'folder' | 'file' | 'pdf';
export type DccStorageRoot = 'uploads' | 'mhidocmails';

export type DccSearchItem = {
  path: string;
  name: string;
  isFolder: boolean;
  size?: number | null;
  created_at?: string | number | null;
  modified_at?: string | number | null;
};

export type DccSearchResponse = {
  items: DccSearchItem[];
  limit: number;
  folders_processed: number;
  truncated: boolean;
  storage_root?: DccStorageRoot;
};

export type DccListStatsItem = {
  name: string;
  path: string;
  is_dir: boolean;
  is_file: boolean;
  size: number;
  modified_at: number;
  created_at: number;
};

export type DccIndexRefreshResponse = {
  success: boolean;
  root?: string;
  storage_root?: DccStorageRoot;
  prefix?: string;
  indexed_rows?: number;
  folders_processed?: number;
  elapsed_ms?: number;
  roots_total?: number;
  ok?: number;
  failed?: number;
  results?: any[];
  error?: string;
};

export type DccOcrResponse = {
  success: boolean;
  engine?: string;
  source_type?: 'pdf' | 'image';
  text?: string;
  text_length?: number;
  page_count?: number;
  pages?: Array<{
    page: number;
    method: 'native' | 'ocr';
    text: string;
    line_count?: number;
    lines?: Array<{
      text: string;
      score?: number | null;
    }>;
  }>;
  message?: string;
};

export type DccExifSummary = {
  make?: string | null;
  model?: string | null;
  software?: string | null;
  captured_at?: string | null;
  orientation?: string | number | null;
  width?: number | string | null;
  height?: number | string | null;
  latitude?: number | null;
  longitude?: number | null;
  lens_model?: string | null;
  iso?: number | string | null;
  f_number?: number | string | null;
  exposure_time?: string | number | null;
  focal_length?: string | number | null;
};

export type DccExifResponse = {
  success: boolean;
  library?: string;
  source_type?: 'upload' | 'server-file';
  file_name?: string;
  file_path?: string | null;
  tag_count?: number;
  summary?: DccExifSummary;
  tags?: Record<string, any>;
  message?: string;
};

type DccBaseUrls = {
  dccApiBase: string;
  apiBase: string;
};

const getBaseUrls = (): DccBaseUrls => {
  const backendBaseUrl = (() => {
    if (process.env.NODE_ENV !== 'development') return '';
    const fromEnv = (process.env.REACT_APP_BACKEND_URL || '').trim();
    const raw = fromEnv || `http://${window.location.hostname}:5000`;
    const trimmed = raw.replace(/\/$/, '');
    return trimmed.replace(/\/api$/i, '');
  })();

  const apiBase = process.env.NODE_ENV === 'development' ? `${backendBaseUrl}/api` : '/api';
  const dccApiBase = `${apiBase}/dcc`;
  return { dccApiBase, apiBase };
};

const normalizeDccPath = (path: string): string => {
  const p = String(path || '');
  return p.replace(/\\/g, '/').replace(/^\/+/, '');
};

const buildAuthHeaders = (token?: string | null): HeadersInit | undefined => {
  if (!token) return undefined;
  return { Authorization: `Bearer ${token}` };
};

const buildAuthHeaderObject = (token?: string | null): Record<string, string> => {
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
};

const appendStorageRoot = (qs: URLSearchParams, root?: DccStorageRoot): void => {
  if (root) {
    qs.set('root', root);
  }
};

const readErrorBody = async (res: Response): Promise<string> => {
  const contentType = res.headers.get('content-type') || '';
  try {
    if (contentType.includes('application/json')) {
      const j = await res.json();
      return typeof j === 'string' ? j : JSON.stringify(j);
    }
    return await res.text();
  } catch {
    return '';
  }
};



export const dccList = async (
  path: string,
  token?: string | null,
  root?: DccStorageRoot,
  signal?: AbortSignal
): Promise<string[]> => {
  const { dccApiBase } = getBaseUrls();
  const normalizedPath = normalizeDccPath(path || '');
  const qs = new URLSearchParams();
  qs.set('path', normalizedPath);
  appendStorageRoot(qs, root);
  const res = await fetch(`${dccApiBase}/list?${qs.toString()}`, {
    headers: buildAuthHeaders(token),
    signal,
  });
  if (!res.ok) {
    const body = await readErrorBody(res);
    throw new Error(`DCC list failed: ${res.status}${body ? ` - ${body}` : ''}`);
  }
  const data: DccJson = await res.json();
  if (!Array.isArray(data)) {
    throw new Error('DCC list returned non-array response');
  }
  return data;
};

export const dccListStats = async (
  path: string,
  token?: string | null,
  root?: DccStorageRoot,
  signal?: AbortSignal
): Promise<DccListStatsItem[]> => {
  const { dccApiBase } = getBaseUrls();
  const normalizedPath = normalizeDccPath(path || '');
  const qs = new URLSearchParams();
  qs.set('path', normalizedPath);
  appendStorageRoot(qs, root);
  const res = await fetch(`${dccApiBase}/list_stats?${qs.toString()}`, {
    headers: buildAuthHeaders(token),
    signal,
  });
  if (!res.ok) {
    const body = await readErrorBody(res);
    throw new Error(`DCC list_stats failed: ${res.status}${body ? ` - ${body}` : ''}`);
  }
  const data: DccJson = await res.json();
  if (!Array.isArray(data)) {
    throw new Error('DCC list_stats returned non-array response');
  }
  return data as DccListStatsItem[];
};

export const dccSearch = async (
  params: {
    q: string;
    qName?: string;
    qPath?: string;
    root?: string;
    storageRoot?: DccStorageRoot;
    type?: DccSearchType;
    limit?: number;
    maxFolders?: number;
    useIndex?: boolean;
  },
  token?: string | null,
  signal?: AbortSignal
): Promise<DccSearchResponse> => {
  const { dccApiBase } = getBaseUrls();
  const qs = new URLSearchParams();
  // Backward compatibility: keep `q` as the generic query, but allow explicit
  // name/path filters via q_name and q_path.
  if (typeof params.q === 'string') {
    qs.set('q', String(params.q || ''));
  }
  if (params.qName) {
    qs.set('q_name', String(params.qName || ''));
  }
  if (params.qPath) {
    qs.set('q_path', String(params.qPath || ''));
  }
  if (params.root) qs.set('root', normalizeDccPath(params.root));
  if (params.storageRoot) qs.set('storage_root', params.storageRoot);
  if (params.type) qs.set('type', params.type);
  if (typeof params.limit === 'number') qs.set('limit', String(params.limit));
  if (typeof params.maxFolders === 'number') qs.set('max_folders', String(params.maxFolders));
  if (typeof params.useIndex === 'boolean') qs.set('use_index', params.useIndex ? '1' : '0');

  const res = await fetch(`${dccApiBase}/search?${qs.toString()}`, {
    headers: buildAuthHeaders(token),
    signal,
  });
  if (!res.ok) {
    const body = await readErrorBody(res);
    throw new Error(`DCC search failed: ${res.status}${body ? ` - ${body}` : ''}`);
  }
  const data: DccJson = await res.json();
  if (!data || typeof data !== 'object' || !Array.isArray(data.items)) {
    throw new Error('DCC search returned invalid response');
  }
  return data as DccSearchResponse;
};

export const dccIndexRefresh = async (
  args: { root?: string; storageRoot?: DccStorageRoot },
  token?: string | null
): Promise<DccIndexRefreshResponse> => {
  const { dccApiBase } = getBaseUrls();
  const body = { root: args?.root, storage_root: args?.storageRoot };
  const res = await fetch(`${dccApiBase}/index/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...buildAuthHeaderObject(token),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const bodyText = await readErrorBody(res);
    throw new Error(`DCC index refresh failed: ${res.status}${bodyText ? ` - ${bodyText}` : ''}`);
  }
  const data: DccJson = await res.json();
  return data as DccIndexRefreshResponse;
};

export type DccPdfStatsRefreshResponse = {
  success: boolean;
  root?: string;
  path?: string;
  recursive?: boolean;
  is_file?: boolean;
  mode?: string;
  pending_batch?: number;
  ok?: number;
  failed?: number;
  upstream_total?: number;
  written?: number;
  pruned?: number;
  elapsed_ms?: number;
  upstream_elapsed_ms?: number;
  results?: Array<{
    success: boolean;
    path?: string;
    root?: string;
    name?: string;
    written?: number;
    elapsed_ms?: number;
    error?: string;
  }>;
  error?: string;
};

export const dccPdfStatsRefresh = async (
  args: {
    root: string;
    path?: string;
    recursive?: boolean;
    missing?: boolean;
    timeout_sec?: number;
  },
  token?: string | null
): Promise<DccPdfStatsRefreshResponse> => {
  const { dccApiBase } = getBaseUrls();
  const body: Record<string, unknown> = {
    root: args.root,
    path: args.path,
    recursive: typeof args.recursive === 'boolean' ? args.recursive : true,
  };
  if (args.missing) {
    body.missing = true;
    body.recursive = false;
    if (typeof args.timeout_sec === 'number') body.timeout_sec = args.timeout_sec;
  }
  const res = await fetch(`${dccApiBase}/pdf-stats/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...buildAuthHeaderObject(token),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const bodyText = await readErrorBody(res);
    throw new Error(`DCC PDF stats refresh failed: ${res.status}${bodyText ? ` - ${bodyText}` : ''}`);
  }
  const data: DccJson = await res.json();
  return data as DccPdfStatsRefreshResponse;
};

export const dccMkdir = async (path: string, token?: string | null): Promise<any> => {
  const { dccApiBase } = getBaseUrls();
  const normalizedPath = normalizeDccPath(path || '');
  const res = await fetch(`${dccApiBase}/mkdir`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...buildAuthHeaderObject(token) },
    body: JSON.stringify({ path: normalizedPath }),
  });
  const bodyText = await readErrorBody(res);
  if (!res.ok) throw new Error(`DCC mkdir failed: ${res.status}${bodyText ? ` - ${bodyText}` : ''}`);
  if (!bodyText) return {};
  try { return JSON.parse(bodyText); } catch { return {}; }
};

export const dccDelete = async (
  path: string,
  token?: string | null,
  root?: DccStorageRoot
): Promise<any> => {
  const { dccApiBase } = getBaseUrls();
  const normalizedPath = normalizeDccPath(path || '');
  const res = await fetch(`${dccApiBase}/delete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...buildAuthHeaderObject(token) },
    body: JSON.stringify({ path: normalizedPath, root }),
  });
  const bodyText = await readErrorBody(res);
  if (!res.ok) throw new Error(`DCC delete failed: ${res.status}${bodyText ? ` - ${bodyText}` : ''}`);
  if (!bodyText) return {};
  try { return JSON.parse(bodyText); } catch { return {}; }
};

export const dccMove = async (
  src: string,
  dst: string,
  token?: string | null,
  root?: DccStorageRoot
): Promise<any> => {
  const { dccApiBase } = getBaseUrls();
  const normalizedSrc = normalizeDccPath(src || '');
  const normalizedDst = normalizeDccPath(dst || '');
  const res = await fetch(`${dccApiBase}/move`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...buildAuthHeaderObject(token) },
    body: JSON.stringify({ src: normalizedSrc, dst: normalizedDst, root }),
  });
  const bodyText = await readErrorBody(res);
  if (!res.ok) throw new Error(`DCC move failed: ${res.status}${bodyText ? ` - ${bodyText}` : ''}`);
  if (!bodyText) return {};
  try { return JSON.parse(bodyText); } catch { return {}; }
};

export const dccCopy = async (src: string, dst: string, token?: string | null): Promise<any> => {
  const { dccApiBase } = getBaseUrls();
  const normalizedSrc = normalizeDccPath(src || '');
  const normalizedDst = normalizeDccPath(dst || '');
  const res = await fetch(`${dccApiBase}/copy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...buildAuthHeaderObject(token) },
    body: JSON.stringify({ src: normalizedSrc, dst: normalizedDst }),
  });
  const bodyText = await readErrorBody(res);
  if (!res.ok) throw new Error(`DCC copy failed: ${res.status}${bodyText ? ` - ${bodyText}` : ''}`);
  if (!bodyText) return {};
  try { return JSON.parse(bodyText); } catch { return {}; }
};

type DccUploadOptions = {
  relativePaths?: string[];
  requireRelativePaths?: boolean;
  /** Sunucuya gönderilen tek zip dosyasını açmasını bildirir (extract_zip=1) */
  extractZip?: boolean;
};

export const dccUpload = async (
  targetPath: string,
  files: File[],
  token?: string | null,
  options?: DccUploadOptions
): Promise<any> => {
  const { dccApiBase } = getBaseUrls();
  const normalizedTargetPath = normalizeDccPath(targetPath || '');
  const formData = new FormData();
  files.forEach((f) => formData.append('files', f));
  formData.append('path', normalizedTargetPath);
  if (options?.requireRelativePaths) {
    formData.append('require_relative_paths', '1');
  }
  if (Array.isArray(options?.relativePaths)) {
    formData.append('relative_paths_json', JSON.stringify(options?.relativePaths));
  }
  if (options?.extractZip) {
    formData.append('extract_zip', '1');
  }

  const res = await fetch(`${dccApiBase}/upload`, {
    method: 'POST',
    body: formData,
    headers: buildAuthHeaders(token),
  });

  const bodyText = await readErrorBody(res);
  if (!res.ok) {
    throw new Error(`DCC upload failed: ${res.status}${bodyText ? ` - ${bodyText}` : ''}`);
  }

  try {
    return bodyText ? JSON.parse(bodyText) : {};
  } catch {
    return {};
  }
};

export const dccUploadTar = async (
  targetPath: string,
  tarBlob: Blob,
  token?: string | null
): Promise<any> => {
  const { dccApiBase } = getBaseUrls();
  const qs = new URLSearchParams();
  qs.set('path', normalizeDccPath(targetPath || ''));

  const res = await fetch(`${dccApiBase}/upload-tar?${qs.toString()}`, {
    method: 'POST',
    body: tarBlob,
    headers: {
      'Content-Type': 'application/x-tar',
      ...buildAuthHeaderObject(token),
    },
  });

  const bodyText = await readErrorBody(res);
  if (!res.ok) {
    throw new Error(`DCC TAR upload failed: ${res.status}${bodyText ? ` - ${bodyText}` : ''}`);
  }
  try {
    return bodyText ? JSON.parse(bodyText) : {};
  } catch {
    return {};
  }
};

export const dccOcr = async (
  args: { file?: File; filePath?: string; forceOcr?: boolean },
  token?: string | null
): Promise<DccOcrResponse> => {
  const { dccApiBase } = getBaseUrls();
  const formData = new FormData();

  if (args.file) {
    formData.append('file', args.file);
  }
  if (args.filePath) {
    formData.append('file_path', normalizeDccPath(args.filePath));
  }
  if (typeof args.forceOcr === 'boolean') {
    formData.append('force_ocr', args.forceOcr ? '1' : '0');
  }

  const res = await fetch(`${dccApiBase}/ocr`, {
    method: 'POST',
    body: formData,
    headers: buildAuthHeaders(token),
  });

  const bodyText = await readErrorBody(res);
  if (!res.ok) {
    throw new Error(`DCC OCR failed: ${res.status}${bodyText ? ` - ${bodyText}` : ''}`);
  }

  try {
    return bodyText ? JSON.parse(bodyText) : { success: false, message: 'Empty OCR response' };
  } catch {
    throw new Error('DCC OCR returned invalid JSON');
  }
};

export const dccReadExif = async (
  args: { file?: File; filePath?: string },
  token?: string | null
): Promise<DccExifResponse> => {
  const { dccApiBase } = getBaseUrls();
  let res: Response;

  if (args.file) {
    const formData = new FormData();
    formData.append('file', args.file);
    res = await fetch(`${dccApiBase}/exif`, {
      method: 'POST',
      body: formData,
      headers: buildAuthHeaders(token),
    });
  } else {
    res = await fetch(`${dccApiBase}/exif`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...buildAuthHeaderObject(token),
      },
      body: JSON.stringify({
        file_path: args.filePath ? normalizeDccPath(args.filePath) : '',
      }),
    });
  }

  const bodyText = await readErrorBody(res);
  if (!res.ok) {
    throw new Error(`DCC EXIF failed: ${res.status}${bodyText ? ` - ${bodyText}` : ''}`);
  }

  try {
    return bodyText ? JSON.parse(bodyText) : { success: false, message: 'Empty EXIF response' };
  } catch {
    throw new Error('DCC EXIF returned invalid JSON');
  }
};

export const dccDownloadUrl = (
  path: string,
  token?: string | null,
  inline?: boolean,
  root?: DccStorageRoot
): string => {
  const { dccApiBase } = getBaseUrls();
  const qs = new URLSearchParams();
  qs.set('path', normalizeDccPath(path || ''));
  if (inline) qs.set('inline', '1');
  if (token) qs.set('token', token);
  appendStorageRoot(qs, root);
  return `${dccApiBase}/download?${qs.toString()}`;
};

export const dccPreviewPdfUrl = (
  path: string,
  token?: string | null,
  root?: DccStorageRoot
): string => {
  const { apiBase } = getBaseUrls();
  const qs = new URLSearchParams();
  qs.set('path', normalizeDccPath(path || ''));
  if (token) qs.set('token', token);
  appendStorageRoot(qs, root);
  return `${apiBase}/dcc/documents/preview?${qs.toString()}`;
};
